/**
 * PrizeAllocationPanel — the green / orange / red breakdown for one event, plus
 * the calculation trail that shows how those figures become the prize total.
 *
 * The trail exists because "Prize pool: $1,088.26" on its own is unauditable. An
 * admin needs to see the whole route — how much each entry contributes, which
 * entries count, what each adjustment takes off, and what the payouts are then
 * computed from. Every number below comes from the backend's shared engine; this
 * component does no prize math of its own, so it cannot drift from the money.
 *
 * The two columns are the point. LOCKED IN prices the pool as if only the
 * settled (drawn-in) entries existed; PROJECTED prices it with every entry that
 * currently counts, and that is the number the per-place payouts come from. A
 * percentage adjustment lands on a different dollar figure in each column —
 * seeing both side by side is what makes the math legible.
 *
 * ACCESSIBILITY: colour is never the only carrier of meaning — every bucket has
 * an icon, a text label, a count and a figure, and the trail is a real table with
 * header cells. The stacked bar is decorative (aria-hidden).
 */

import { Lock, Clock, Ban, AlertTriangle, Info, Calculator } from 'lucide-react';
import {
  PRIZE_STATUS_LABELS,
  PRIZE_STATUS_DESCRIPTIONS,
  PRIZE_STATUS_STYLES,
  type PrizeAllocationStatus,
} from '@/types/prizeAllocation';
import type {
  PrizeAllocationBreakdown,
  PrizeCalculationTrail,
} from '@/api/extensions/usePrizeOverview';

const money = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** A signed adjustment delta: "+$500.00" / "−$28.45". */
const signedMoney = (n: number) => `${n < 0 ? '−' : '+'}${money(Math.abs(n))}`;

const STATUS_ICONS: Record<PrizeAllocationStatus, typeof Lock> = {
  locked: Lock,
  potential: Clock,
  excluded: Ban,
};

interface BucketProps {
  status: PrizeAllocationStatus;
  amount: number;
  count: number;
  /** What the figure above actually is, e.g. "4 × $211.20". */
  basis?: string | null;
  /** Extra line under the figures, e.g. the exclusion-reason split. */
  detail?: string | null;
}

function Bucket({ status, amount, count, basis, detail }: BucketProps) {
  const Icon = STATUS_ICONS[status];
  const styles = PRIZE_STATUS_STYLES[status];

  return (
    <div className="flex-1 min-w-[160px] rounded-md border border-border/60 bg-background p-3">
      <div className="flex items-center gap-1.5">
        <Icon className={`h-3.5 w-3.5 shrink-0 ${styles.text}`} aria-hidden="true" />
        <span className={`text-xs font-medium ${styles.text}`}>
          {PRIZE_STATUS_LABELS[status]}
        </span>
      </div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{money(amount)}</div>
      <div className="text-xs text-muted-foreground">
        {count} {count === 1 ? 'entry' : 'entries'}
        {basis && <span className="tabular-nums"> · {basis}</span>}
      </div>
      {detail && <div className="mt-1 text-xs text-muted-foreground">{detail}</div>}
      <span className="sr-only">{PRIZE_STATUS_DESCRIPTIONS[status]}</span>
    </div>
  );
}

/** Plain-English statement of what one entry puts into the pool. */
function perEntryBasisText(math: PrizeCalculationTrail): string | null {
  switch (math.per_entry_basis) {
    case 'flat':
      return `a flat ${money(math.per_entry_value)} per entry`;
    case 'percentage_of_entry_fee':
      return `${math.per_entry_value}% of the ${money(math.entry_fee)} entry fee`;
    case 'entry_fee_less_deduction':
      return `the ${money(math.entry_fee)} entry fee less ${money(math.per_entry_value)}`;
    default:
      return null;
  }
}

/**
 * The calculation trail. Two priced columns for a dynamic pool; one for a fixed
 * pool, whose total is the same either way because headcount cannot move it.
 */
function CalculationTrail({ math }: { math: PrizeCalculationTrail }) {
  const isDynamic = math.allocation_mode === 'dynamic';
  const basisText = perEntryBasisText(math);

  const potentialCount = math.projected.entry_count - math.locked.entry_count;
  const countingTotal = math.projected.entry_count;

  // A cell showing an adjustment's effect in one column.
  const deltaCell = (delta: number) => (
    <td
      className={`py-1 pl-3 text-right tabular-nums ${
        delta < 0
          ? 'text-red-700 dark:text-red-400'
          : 'text-emerald-700 dark:text-emerald-400'
      }`}
    >
      {signedMoney(delta)}
    </td>
  );

  return (
    <div className="rounded-md border border-border/60">
      <div className="flex items-center gap-1.5 border-b border-border/60 bg-muted/40 px-3 py-2">
        <Calculator className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        <h4 className="text-xs font-semibold">How this prize is calculated</h4>
      </div>

      <div className="space-y-2 px-3 py-2">
        {/* Where the money comes from, before anyone is counted. */}
        <p className="text-xs">
          {isDynamic ? (
            <>
              Each entry in the pool contributes{' '}
              <span className="font-semibold tabular-nums">
                {money(math.per_entry_dollars)}
              </span>
              {basisText && (
                <span className="text-muted-foreground"> — {basisText}</span>
              )}
              .
            </>
          ) : (
            <>
              Fixed prize of{' '}
              <span className="font-semibold tabular-nums">
                {money(math.fixed_prize_amount)}
              </span>
              <span className="text-muted-foreground">
                {' '}
                — the number of entries does not change it.
              </span>
            </>
          )}
        </p>

        {/* The counting rule, stated. Every figure below depends on it. */}
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            {countingTotal} {countingTotal === 1 ? 'entry counts' : 'entries count'} toward
            the pool
          </span>{' '}
          ({math.locked.entry_count} locked in + {potentialCount} potential). Entries that
          are cancelled, drew out, did not perform, or were removed by an admin do not
          count
          {math.excluded.entry_count > 0 && (
            <>
              {' '}
              — {math.excluded.entry_count}{' '}
              {math.excluded.entry_count === 1 ? 'entry is' : 'entries are'} out
              {isDynamic && (
                <>
                  , keeping{' '}
                  <span className="tabular-nums">{money(math.excluded.gross)}</span> out of
                  the pool
                </>
              )}
            </>
          )}
          .
        </p>

        <table className="w-full text-xs">
          <caption className="sr-only">
            Prize calculation, from entry contributions through each adjustment to the
            total.
          </caption>
          <thead>
            <tr className="border-b border-border/60 text-muted-foreground">
              <th scope="col" className="py-1 text-left font-medium">
                Step
              </th>
              <th scope="col" className="py-1 pl-3 text-right font-medium">
                {isDynamic ? 'Locked in' : 'Amount'}
              </th>
              {isDynamic && (
                <th scope="col" className="py-1 pl-3 text-right font-medium">
                  Projected
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border/40">
              <th scope="row" className="py-1 text-left font-normal">
                {isDynamic ? 'Entry contributions' : 'Fixed prize'}
                {isDynamic && (
                  <span className="block text-[11px] text-muted-foreground tabular-nums">
                    {math.locked.entry_count} vs {math.projected.entry_count} entries ×{' '}
                    {money(math.per_entry_dollars)}
                  </span>
                )}
              </th>
              <td className="py-1 pl-3 text-right tabular-nums">
                {money(math.locked.gross)}
              </td>
              {isDynamic && (
                <td className="py-1 pl-3 text-right tabular-nums">
                  {money(math.projected.gross)}
                </td>
              )}
            </tr>

            {math.adjustments.map((adj, i) => (
              <tr key={`${adj.label}-${i}`} className="border-b border-border/40">
                <th scope="row" className="py-1 text-left font-normal">
                  {adj.label || 'Adjustment'}
                  <span className="ml-1.5 text-muted-foreground tabular-nums">
                    {adj.adjustment_type === 'subtraction' ? '−' : '+'}
                    {adj.is_percentage ? `${adj.value}%` : money(adj.value)}
                  </span>
                  {adj.source === 'org_default' && (
                    <span className="ml-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      org default
                    </span>
                  )}
                </th>
                {deltaCell(adj.locked_delta)}
                {isDynamic && deltaCell(adj.projected_delta)}
              </tr>
            ))}

            <tr className="font-semibold">
              <th scope="row" className="py-1.5 text-left">
                Prize total
              </th>
              <td className="py-1.5 pl-3 text-right tabular-nums">
                {money(math.locked.total)}
              </td>
              {isDynamic && (
                <td className="py-1.5 pl-3 text-right tabular-nums">
                  {money(math.projected.total)}
                </td>
              )}
            </tr>
          </tbody>
        </table>

        {/*
          Percentages are taken off the post-dollar subtotal, which is why the
          same "−4%" is a different number in each column. Say so, rather than
          leaving an admin to reconcile two figures that look inconsistent.
        */}
        {isDynamic && math.adjustments.some((a) => a.is_percentage) && (
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              Percentage adjustments apply after every dollar adjustment, so they come
              off a different subtotal in each column. The payouts by round below are
              calculated from the projected total of {money(math.projected.total)}.
            </span>
          </p>
        )}
      </div>
    </div>
  );
}

export interface PrizeAllocationPanelProps {
  breakdown: PrizeAllocationBreakdown;
  /** The step-by-step route from entry count to prize total. */
  math: PrizeCalculationTrail;
  /** Scroll the contestant list to the entries needing a decision. */
  onReviewFlagged?: () => void;
}

export function PrizeAllocationPanel({
  breakdown,
  math,
  onReviewFlagged,
}: PrizeAllocationPanelProps) {
  const {
    allocation_mode,
    draw_finalized,
    locked_amount,
    potential_amount,
    excluded_amount,
    projected_total,
    locked_count,
    potential_count,
    excluded_count,
    excluded_by_reason,
    needs_review_count,
  } = breakdown;

  const isDynamic = allocation_mode === 'dynamic';

  // The buckets show what the ENTRIES contribute — the raw headcount money,
  // before any adjustment lands. The adjusted totals are the trail's job, and
  // showing both is the whole ask: an admin can now follow one into the other.
  //
  // A fixed pool has no per-entry contribution to show (entries move no money),
  // so it keeps reporting the pool itself, which is the only figure that means
  // anything there.
  const potentialGross = math.projected.gross - math.locked.gross;
  const buckets = isDynamic
    ? {
        locked: math.locked.gross,
        potential: potentialGross,
        excluded: math.excluded.gross,
      }
    : { locked: locked_amount, potential: potential_amount, excluded: excluded_amount };

  const perEntry = money(math.per_entry_dollars);
  const basisFor = (count: number) => (isDynamic ? `${count} × ${perEntry}` : null);

  // Bar widths. Guard the zero-pool case so we don't divide by zero and render
  // a NaN-width bar.
  const barBase = buckets.locked + buckets.potential + buckets.excluded;
  const pct = (n: number) => (barBase > 0 ? (n / barBase) * 100 : 0);

  const excludedDetail =
    excluded_count > 0
      ? [
          excluded_by_reason.cancelled > 0 && `${excluded_by_reason.cancelled} cancelled`,
          excluded_by_reason.did_not_perform > 0 &&
            `${excluded_by_reason.did_not_perform} did not perform`,
          excluded_by_reason.drawn_out > 0 && `${excluded_by_reason.drawn_out} drew out`,
          excluded_by_reason.removed_from_prize_pool > 0 &&
            `${excluded_by_reason.removed_from_prize_pool} removed from pool`,
        ]
          .filter(Boolean)
          .join(' · ')
      : null;

  return (
    <div className="space-y-3">
      {/* Draw state, in words. Every number below depends on it. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="inline-flex items-center gap-1.5 font-medium">
          {draw_finalized ? (
            <>
              <Lock className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
              Draw is final
            </>
          ) : (
            <>
              <Clock className="h-3.5 w-3.5 text-amber-600" aria-hidden="true" />
              Draw is not final — nothing is locked in yet
            </>
          )}
        </span>
        <span className="text-muted-foreground">
          {isDynamic
            ? 'Dynamic pool — grows with each entry'
            : 'Fixed pool — does not change with entry count'}
        </span>
        <span className="ml-auto font-semibold tabular-nums">
          Prize pool: {money(projected_total)}
        </span>
      </div>

      {/* Decorative: the same information is in the buckets below. */}
      {barBase > 0 && (
        <div
          className="flex h-2 w-full overflow-hidden rounded-full bg-muted"
          aria-hidden="true"
        >
          <div
            className={PRIZE_STATUS_STYLES.locked.bar}
            style={{ width: `${pct(buckets.locked)}%` }}
          />
          <div
            className={PRIZE_STATUS_STYLES.potential.bar}
            style={{ width: `${pct(buckets.potential)}%` }}
          />
          <div
            className={PRIZE_STATUS_STYLES.excluded.bar}
            style={{ width: `${pct(buckets.excluded)}%` }}
          />
        </div>
      )}

      {isDynamic && (
        <p className="text-xs text-muted-foreground">
          Entry contributions, before adjustments. The adjustments and the resulting
          totals are below.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Bucket
          status="locked"
          amount={buckets.locked}
          count={locked_count}
          basis={basisFor(locked_count)}
        />
        <Bucket
          status="potential"
          amount={buckets.potential}
          count={potential_count}
          basis={basisFor(potential_count)}
        />
        <Bucket
          status="excluded"
          amount={buckets.excluded}
          count={excluded_count}
          basis={basisFor(excluded_count)}
          detail={excludedDetail}
        />
      </div>

      <CalculationTrail math={math} />

      {/*
        A fixed pool doesn't move with headcount, so the excluded column is
        always $0 — say so, rather than letting an admin read the zero as
        "nobody was excluded".
      */}
      {!isDynamic && excluded_count > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            {excluded_count} {excluded_count === 1 ? 'entry is' : 'entries are'} out of
            the prize pool, but this is a fixed pool — removing entries doesn&apos;t
            change the payout.
          </span>
        </p>
      )}

      {/*
        Drawing out and DNP now pull an entry from the pool automatically, so an
        entry that carries one of those flags and is STILL counted got put back
        deliberately — by the admin's own toggle here, or by the Exclude button
        in live scoring. Worth a second look, which is what this surfaces.
      */}
      {needs_review_count > 0 && (
        <button
          type="button"
          onClick={onReviewFlagged}
          className="flex w-full items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-2 text-left text-xs text-amber-900 transition-colors hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200 dark:hover:bg-amber-950"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            <span className="font-medium">
              {needs_review_count}{' '}
              {needs_review_count === 1 ? 'entry needs' : 'entries need'} review
            </span>{' '}
            — counted in the prize pool even though they drew out, did not perform, or
            got no spot in the draw. Review them below.
          </span>
        </button>
      )}
    </div>
  );
}
